import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const FIELDS = [["sale_date", "Date", "date"], ["customer_name", "Customer", "text"], ["tons", "Tons", "number"], ["vehicle_no", "Vehicle No", "text"]];

export const WebsiteEditDialog = ({ sale, month, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [review, setReview] = useState(false);
  const [ok, setOk] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (sale) { setForm({ sale_date: sale.sale_date, customer_name: sale.customer_name, tons: sale.tons, vehicle_no: sale.vehicle_no || "" }); setReview(false); setOk(false); setReason(""); }
  }, [sale]);

  if (!sale) return null;
  const val = (k) => (k === "tons" ? Number(form[k]) : form[k]);
  const changed = FIELDS.filter(([k]) => String(val(k)) !== String(sale[k] ?? ""));

  const save = async () => {
    setSaving(true);
    try {
      const body = { sale_id: sale.id, month, reason };
      changed.forEach(([k]) => { body[k] = val(k); });
      await api.post("/verification/website-edit", body);
      toast.success("Website entry updated and logged in audit history");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="website-edit-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-red-700">Edit Website Entry</DialogTitle>
          <DialogDescription>This changes a real sale record ({sale.plant}). Use only when the book is correct and the website is wrong.</DialogDescription>
        </DialogHeader>
        {!review ? (
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, label, type]) => (
              <div key={k}><Label>{label}</Label>
                <Input type={type} step="0.001" data-testid={`website-edit-${k}`} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
            ))}
            <div className="col-span-2"><Label>Reason (optional)</Label><Input data-testid="website-edit-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Book shows 12 tons" /></div>
          </div>
        ) : (
          <div className="space-y-3">
            <table data-testid="website-edit-review" className="w-full text-sm border rounded">
              <thead><tr className="bg-slate-50 text-left"><th className="p-2">Field</th><th className="p-2">Original</th><th className="p-2">Proposed</th></tr></thead>
              <tbody>{changed.map(([k, label]) => (
                <tr key={k} className="border-t"><td className="p-2">{label}</td><td className="p-2 text-red-700 line-through">{String(sale[k] ?? "")}</td><td className="p-2 text-emerald-700 font-semibold">{String(val(k))}</td></tr>
              ))}</tbody>
            </table>
            <label className="flex items-start gap-2 text-sm"><Checkbox data-testid="website-edit-confirm-check" checked={ok} onCheckedChange={(v) => setOk(!!v)} />I confirm this change to the actual website sale record.</label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => (review ? setReview(false) : onClose())}>{review ? "Back" : "Cancel"}</Button>
          {!review
            ? <Button data-testid="website-edit-review-button" disabled={!changed.length} onClick={() => setReview(true)}>Review Change</Button>
            : <Button data-testid="website-edit-save-button" className="bg-red-600 hover:bg-red-700" disabled={!ok || saving} onClick={save}>{saving ? "Saving…" : "Confirm & Save"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
